import { LineItem } from '@commercetools/platform-sdk';
import { DrupalJsonApiParams } from 'drupal-jsonapi-params';
import { DrupalProductReviewRatings } from './types';

/**
 * Generate a Drupal JSON:API query string to fetch Commerce Product records
 * for the given CommerceTools line item product keys.
 *
 * The returned Commerce Product data is used to map:
 * CommerceTools lineItem.productKey -> Drupal field_ratings_api_id.
 *
 * @param lineItems
 *   CommerceTools order/cart line items.
 *
 * @return
 *   Query string for the Drupal JSON:API commerce_product endpoint.
 */
export const getProductReviewRatingsQueryString = (
  lineItems: LineItem[],
): string => {
  const apiParams = new DrupalJsonApiParams();

  apiParams.addGroup('group1', 'OR');

  lineItems.forEach((lineItem) => {
    if (lineItem.productKey) {
      apiParams.addFilter(
        'field_product_key',
        lineItem.productKey,
        '=',
        'group1',
      );
    }
  });

  apiParams.addFields('commerce_product--default', [
    'field_product_key',
    'field_ratings_api_id',
  ]);

  return apiParams.getQueryString();
};

/**
 * Parse the Drupal Commerce Product JSON:API response and return a lookup map.
 *
 * The result maps:
 * CommerceTools productKey -> Drupal field_ratings_api_id.
 *
 * @param productReviewRatings
 *   Drupal JSON:API response from the commerce_product endpoint.
 *
 * @return
 *   Product review ratings API ID lookup keyed by CommerceTools productKey.
 */
export const parseProductReviewRatingsResponse = (
  productReviewRatings: DrupalProductReviewRatings,
): Record<string, string> => {
  const result: Record<string, string> = {};

  productReviewRatings?.data?.forEach((product) => {
    const productKey = product?.attributes?.field_product_key;
    const ratingsApiId = product?.attributes?.field_ratings_api_id;

    if (productKey && ratingsApiId) {
      result[productKey] = ratingsApiId;
    }
  });

  return result;
};
